import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import moment from 'moment'
import { isEmpty } from 'lodash'
import { IconX } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { AGENT_SIDE } from '@/reducers/realtime/liveTranscripts.helpers'
import {
  closeTranscript,
  watchTranscript,
  unwatchTranscript,
  selectAgentTranscript,
  selectAgentCallEnded,
} from '@/reducers/realtime/liveTranscripts.redux'

/**
 * Live transcript of the call the Agent Assist widget is currently focused on.
 *
 * It is a sibling of the widget rather than a pane inside it: the two open and
 * close independently, and the transcript stays readable with the widget shut
 * (it just slides right to take its place).
 *
 * Utterances arrive over whichever realtime socket the manager has — the RTC
 * socket pushes every supervised agent unprompted, while the Command Center
 * socket relays only the agent we ask for, hence the watch below.
 */
export const LiveTranscript = () => {
  const dispatch = useDispatch()
  const { open, byAgent, watchStatus, endedAgents } = useSelector((state) => state.liveTranscripts)
  const {
    chats = {},
    activeUsername = null,
    panelOpen = false,
  } = useSelector((state) => state.realtimeChats)
  const { socketOpen: ccSocketOpen } = useSelector((state) => state.commandCenter)
  const bodyRef = useRef(null)

  const activeChat = chats[activeUsername] || null
  // The record identity churns on every message; only its existence matters to
  // the watch effect, and re-running it would unwatch/rewatch mid-call and lose
  // whatever the server published inside the gap.
  const hasActiveChat = Boolean(activeChat)
  const utterances = selectAgentTranscript({ liveTranscripts: { byAgent } }, activeUsername)
  // The chat outlives the call, so the panel stays open after hangup. Say so
  // rather than leaving the last words looking like they are still arriving.
  const callEnded = selectAgentCallEnded({ liveTranscripts: { endedAgents } }, activeUsername)
  const agentFirstName = activeChat?.agentFirstName || 'Agent'
  const latestUtterance = utterances[utterances.length - 1]
  const denial =
    watchStatus[activeUsername]?.status === 'denied' ? watchStatus[activeUsername] : null

  // Ask the Command Center socket for this agent's utterances only while the
  // panel is showing them, and release the watch when it closes or moves on.
  // The watch lives on the socket, so a reconnect has to re-arm it — hence the
  // dependency on `ccSocketOpen` rather than firing once on mount.
  useEffect(() => {
    if (!open || !hasActiveChat || !ccSocketOpen) {
      return undefined
    }

    dispatch(watchTranscript(activeUsername))

    return () => dispatch(unwatchTranscript(activeUsername))
  }, [open, hasActiveChat, activeUsername, ccSocketOpen, dispatch])

  // The chat can be ended out from under an open panel. Leaving `open` true
  // would pop the transcript back up unasked on the next chat, which the design
  // explicitly rules out.
  useEffect(() => {
    if (open && !hasActiveChat) {
      dispatch(closeTranscript())
    }
  }, [open, hasActiveChat, dispatch])

  // Follow the tail. Keyed on the newest block's end time as well as the array
  // length: a run from one speaker merges into the existing block and never
  // grows the array.
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight
    }
    // `callEnded` too, so the "Call ended" marker scrolls into view.
  }, [utterances.length, latestUtterance?.endTime, callEnded])

  if (!open || !activeChat) {
    return null
  }

  return (
    <div
      className={classNames('live-transcript', { 'live-transcript--beside-panel': panelOpen })}
      data-testid="live-transcript"
    >
      <div className="live-transcript__header">
        <div className="live-transcript__heading">
          <div className="live-transcript__title">
            <span
              className={classNames('live-transcript__live-dot', {
                'live-transcript__live-dot--ended': callEnded,
              })}
            />
            {callEnded ? 'Transcript' : 'Live transcript'}
          </div>
          <div className="live-transcript__agent">
            {`${activeChat.agentFirstName} ${activeChat.agentLastName}`}
          </div>
        </div>
        <Button
          square
          small
          basic
          data-testid="live-transcript-close"
          tooltip="Hide transcript"
          onClick={() => dispatch(closeTranscript())}
        >
          <IconX />
        </Button>
      </div>
      <div className="live-transcript__body" ref={bodyRef}>
        {denial && (
          <div className="live-transcript__denied" data-testid="live-transcript-denied">
            {`This call can't be transcribed here${denial.reason ? ` — ${denial.reason}` : ''}.`}
          </div>
        )}
        {isEmpty(utterances) && !denial ? (
          <div className="live-transcript__empty" data-testid="live-transcript-empty">
            {callEnded ? 'This call has ended.' : 'Waiting for the next thing said on this call…'}
          </div>
        ) : (
          utterances.map((utterance) => (
            <div
              className="live-transcript__utterance"
              key={utterance.id}
              data-testid="live-transcript-utterance"
            >
              {utterance.droppedBefore && (
                <div className="live-transcript__gap" data-testid="live-transcript-gap">
                  Some of this call didn&apos;t come through
                </div>
              )}
              <div className="live-transcript__speaker-row">
                <span
                  className={classNames('live-transcript__speaker', {
                    'live-transcript__speaker--agent': utterance.side === AGENT_SIDE,
                  })}
                >
                  {utterance.side === AGENT_SIDE ? agentFirstName : 'Customer'}
                </span>
                <span className="live-transcript__time">{moment(utterance.time).format('LT')}</span>
              </div>
              <div className="live-transcript__text">{utterance.text}</div>
            </div>
          ))
        )}
        {callEnded && !isEmpty(utterances) && (
          <div className="live-transcript__ended" data-testid="live-transcript-ended">
            Call ended
          </div>
        )}
      </div>
    </div>
  )
}
